/**
 * PDFViewerPanelSavedMessageView Component - Saved message view (no PDF)
 *
 * Extracted from PDFViewerPanel.tsx
 */

import React from 'react';
import { MessageSquare, StickyNote, Tag, Info, Copy } from 'lucide-react';
import MarkdownRenderer from '../MarkdownRenderer';
import UsageStats from '../UsageStats';
import type { Theme } from '../../themes/theme';
import { parseSavedMessageMarkdown } from '../../utils/savedMessageMarkdown';
import { PDFViewerPanelMessageRenderer } from './PDFViewerPanelMessageRenderer';
import { PDFViewerPanelReferences } from './PDFViewerPanelReferences';
import type { CitationData, UsageStatsData, RagStatsData } from './types/pdf-viewer-panel.types';

interface PDFViewerPanelSavedMessageViewProps {
  savedMessageMarkdown: string;
  detailTitle?: string;
  detailText?: string;
  detailMetadata: Record<string, any>;
  savedMessageMetadata: Record<string, any>;
  setSavedMessageMetadata: (metadata: Record<string, any>) => void;
  onCitationClick?: (citation: CitationData) => void;
  onChunkClick?: (citation: CitationData) => void;
  referencesExpanded: boolean;
  setReferencesExpanded: (expanded: boolean) => void;
  usageStats?: UsageStatsData | null;
  ragStats?: RagStatsData | null;
  isAdmin: boolean;
  onCopy: () => void;
  theme: Theme;
}

export const PDFViewerPanelSavedMessageView: React.FC<PDFViewerPanelSavedMessageViewProps> = ({
  savedMessageMarkdown,
  detailTitle,
  detailText,
  detailMetadata,
  savedMessageMetadata,
  setSavedMessageMetadata,
  onCitationClick,
  onChunkClick,
  referencesExpanded,
  setReferencesExpanded,
  usageStats,
  ragStats,
  isAdmin,
  onCopy,
  theme,
}) => {
  const parsed = parseSavedMessageMarkdown(savedMessageMarkdown);
  const mergedMetadata = { ...savedMessageMetadata, ...(parsed.data as Record<string, any>) };
  const title =
    mergedMetadata.title || detailTitle || (detailMetadata?.title as string) || 'Mensaje guardado';
  const notes =
    typeof mergedMetadata.notes === 'string'
      ? mergedMetadata.notes
      : typeof detailMetadata?.notes === 'string'
        ? detailMetadata.notes
        : undefined;
  const tags = Array.isArray(mergedMetadata.tags)
    ? mergedMetadata.tags
    : Array.isArray(detailMetadata?.tags)
      ? detailMetadata.tags
      : undefined;
  const citations =
    (mergedMetadata.citations as Record<string, CitationData> | undefined) ||
    (detailMetadata?.citations as Record<string, CitationData> | undefined) ||
    {};
  const createdAtValue =
    (mergedMetadata.created_at as string | undefined) ||
    (detailMetadata?.created_at as string | undefined);
  const displayTimestamp = createdAtValue ? new Date(createdAtValue) : undefined;
  const displayContent = parsed.content || detailText || '';
  const excludedFromContext = Boolean(detailMetadata?.excluded_from_context);
  const sourcesCount =
    typeof detailMetadata?.sources_count === 'number' ? detailMetadata.sources_count : undefined;

  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="max-w-4xl mx-auto w-full space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="text-xs tracking-wide font-semibold flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Mensaje guardado
              </span>
              <h3 className={`text-lg font-semibold mt-1 ${theme.colors.text.primary}`}>{title}</h3>
            </div>
            {displayContent && (
              <button
                onClick={onCopy}
                className={`px-3 py-1.5 rounded-lg border ${theme.colors.border.primary} ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} transition-colors text-xs font-medium flex items-center gap-1`}
              >
                <Copy className="w-3 h-3" />
                Copiar
              </button>
            )}
          </div>

          <div className="flex justify-start">
            <div
              className={`relative max-w-3xl w-full px-5 py-3 rounded-xl ${theme.colors.bg.secondary} ${theme.colors.text.primary} border ${theme.colors.border.primary}`}
            >
              {excludedFromContext && (
                <span
                  className={`inline-block px-2 py-0.5 mb-2 text-xs rounded-full border ${theme.colors.border.primary} ${theme.colors.text.secondary}`}
                >
                  Excluido del contexto
                </span>
              )}

              {displayContent && (
                <MarkdownRenderer
                  content={savedMessageMarkdown || displayContent}
                  onMetadata={(data) => setSavedMessageMetadata(data)}
                  className="text-sm"
                />
              )}

              {displayTimestamp && (
                <p className={`text-xs mt-2 ${theme.colors.text.secondary}`}>
                  {displayTimestamp.toLocaleString('es-ES')}
                </p>
              )}

              {usageStats && (
                <UsageStats stats={usageStats} ragStats={ragStats || undefined} isAdmin={isAdmin} />
              )}

              {citations && Object.keys(citations).length > 0 && (
                <PDFViewerPanelReferences
                  citations={citations}
                  expanded={referencesExpanded}
                  onToggleExpanded={() => setReferencesExpanded(!referencesExpanded)}
                  onCitationClick={onCitationClick}
                  theme={theme}
                />
              )}
            </div>
          </div>

          {(notes || (tags && tags.length) || sourcesCount !== undefined) && (
            <div className="space-y-4">
              {notes && (
                <div>
                  <label
                    className={`text-xs tracking-wide font-semibold flex items-center gap-2 ${theme.colors.text.secondary}`}
                  >
                    <StickyNote className="w-4 h-4" />
                    Notas
                  </label>
                  <div
                    className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg p-3`}
                  >
                    <p
                      className={`text-sm ${theme.colors.text.primary} whitespace-pre-wrap leading-relaxed`}
                    >
                      {notes}
                    </p>
                  </div>
                </div>
              )}

              {tags && tags.length > 0 && (
                <div>
                  <label
                    className={`text-xs tracking-wide font-semibold flex items-center gap-2 ${theme.colors.text.secondary}`}
                  >
                    <Tag className="w-4 h-4" />
                    Etiquetas
                  </label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className={`px-2 py-1 rounded-full text-xs font-medium ${theme.colors.bg.tertiary} ${theme.colors.text.inverse}`}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {sourcesCount !== undefined && (
                <div className="text-xs space-y-1">
                  <div className={`flex items-center gap-2 ${theme.colors.text.secondary}`}>
                    <Info className="w-4 h-4" />
                    <span className="font-semibold">Detalles</span>
                  </div>
                  <div className={`pl-6 space-y-1 ${theme.colors.text.secondary}`}>
                    <p>Fuentes vinculadas: {sourcesCount}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
